/*
 * Velvet Hammer product page v2 (sections/vh-pdp-main.liquid).
 *
 * Gallery, pack and purchase state, mobile pinned bar, "Make it a potion" cart actions.
 * It never sets a selling plan itself: the Stay AI widget (assets/stayai-subscription-clone.js)
 * writes selling_plan into #vh-pdp-product-form. This file only sets the variant id input on that
 * form, re-reads what the widget selected, and tells the rest of the page.
 *
 * Contract for other sections: document event "vh-pdp:selection-change", detail
 * { variantId, price, compareAtPrice, sellingPlanId, label, available } (prices are integer cents,
 * compareAtPrice and sellingPlanId are null when there is none, available is a boolean).
 * The latest detail is also kept on window.vhPdpSelection for scripts that load later.
 */

const SELECTION_EVENT = 'vh-pdp:selection-change';

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function parseJson(text, fallback) {
  try {
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}

/* ---------- Gallery ---------- */

function setupGallery(gallery) {
  const track = gallery?.querySelector('[data-vh-track]');
  if (!gallery || !track) return;

  const slides = Array.from(track.querySelectorAll('[data-vh-slide]'));
  const thumbs = Array.from(gallery.querySelectorAll('[data-vh-thumb]'));
  const current = gallery.querySelector('[data-vh-current]');
  const status = gallery.querySelector('[data-vh-status]');
  let index = 0;
  let frame = 0;

  const goTo = (target) => {
    const next = Math.max(0, Math.min(slides.length - 1, target));
    track.scrollTo({ left: next * track.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  const showThumb = (thumb) => {
    const box = thumb.parentElement;
    if (!box) return;
    const thumbRect = thumb.getBoundingClientRect();
    const boxRect = box.getBoundingClientRect();
    box.scrollLeft += thumbRect.left - boxRect.left - (boxRect.width - thumbRect.width) / 2;
  };

  const update = () => {
    frame = 0;
    const next = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
    if (next === index) return;
    index = next;
    if (current) current.textContent = String(index + 1);
    if (status) status.textContent = `Slide ${index + 1} of ${slides.length}`;
    thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (thumbs[index]) showThumb(thumbs[index]);
  };

  track.addEventListener(
    'scroll',
    () => {
      if (!frame) frame = requestAnimationFrame(update);
    },
    { passive: true }
  );

  thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));

  track.addEventListener('keydown', (event) => {
    if (event.target !== track) return;
    if (event.key === 'ArrowRight') goTo(index + 1);
    else if (event.key === 'ArrowLeft') goTo(index - 1);
    else if (event.key === 'Home') goTo(0);
    else if (event.key === 'End') goTo(slides.length - 1);
    else return;
    event.preventDefault();
  });

  window.addEventListener('resize', () => {
    track.scrollTo({ left: index * track.clientWidth, behavior: 'auto' });
  });

  /* Mouse drag (touch and keyboard already work natively). */
  let down = false;
  let moved = false;
  let startX = 0;
  let startLeft = 0;
  track.addEventListener('mousedown', (event) => {
    if (event.button !== 0) return;
    down = true;
    moved = false;
    startX = event.pageX;
    startLeft = track.scrollLeft;
  });
  window.addEventListener('mousemove', (event) => {
    if (!down) return;
    if (!moved && Math.abs(event.pageX - startX) < 5) return;
    moved = true;
    track.classList.add('is-dragging');
    event.preventDefault();
    track.scrollLeft = startLeft - (event.pageX - startX);
  });
  window.addEventListener('mouseup', () => {
    if (!down) return;
    down = false;
    if (moved) {
      track.classList.remove('is-dragging');
      goTo(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
    }
  });
  track.addEventListener(
    'click',
    (event) => {
      if (moved) {
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      }
    },
    true
  );

  /* Video slides: the player is in an inert <template>; it is created on tap only. */
  gallery.querySelectorAll('[data-vh-video]').forEach((button) => {
    button.addEventListener('click', () => {
      const template = button.parentElement?.querySelector('template[data-vh-video-template]');
      if (!template) return;
      const slide = button.parentElement;
      const player = template.content.cloneNode(true);
      button.remove();
      slide.appendChild(player);
      const video = slide.querySelector('video');
      if (video) {
        video.play?.().catch(() => {});
        video.focus?.({ preventScroll: true });
      } else {
        slide.querySelector('iframe')?.focus?.({ preventScroll: true });
      }
    });
  });
}

function initGallery(root) {
  const outer = root.querySelector('[data-vh-gallery]');
  if (!outer) return;
  const sets = Array.from(outer.querySelectorAll('[data-vh-gallery-set]'));
  if (!sets.length) { setupGallery(outer); return; }
  sets.forEach(setupGallery);
  const follow = (detail) => {
    const id = String(detail?.variantId ?? '');
    const variants = parseJson(outer.querySelector('[data-vh-gallery-variants]')?.textContent || '{}', {});
    const flavor = (variants[id] || '').trim().toLowerCase().replace(/^hint of /, '');
    const key = ['peach', 'lemon honey', 'strawberry'].includes(flavor) ? flavor : 'original';
    const selected = sets.find((set) => set.dataset.vhGalleryFlavor === key) || sets[0];
    sets.forEach((set) => {
      const hide = set !== selected;
      set.hidden = hide;
      if (hide) set.querySelectorAll('video').forEach((video) => video.pause());
    });
  };
  document.addEventListener(SELECTION_EVENT, (event) => follow(event.detail));
  if (window.vhPdpSelection) follow(window.vhPdpSelection);
}

/* The store's own money format, passed from Liquid (same formatter as the science section). */
function moneyFormatter(root) {
  const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
  const withDelimiters = (cents, precision, thousands = ',', decimal = '.') => {
    const fixed = (cents / 100).toFixed(precision).split('.');
    return fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`) + (fixed[1] ? decimal + fixed[1] : '');
  };
  const fmt = (cents) => {
    const kind = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
    let value;
    switch (kind) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2);
    }
    return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  };
  return fmt;
}

/* ---------- Pack, purchase type, mobile bar ---------- */

function initPurchase(root) {
  const form = root.querySelector('#vh-pdp-product-form');
  const variantInput = form?.querySelector('[data-vh-variant-input]');
  if (!form || !variantInput) return;

  const scope = root.closest('.shopify-section') || document;
  const parsed = parseJson(scope.querySelector('[data-vh-variants]')?.textContent || '[]', []);
  /* Invalid or empty variant data must never stop the page: every reader below copes with no variant. */
  const variants = Array.isArray(parsed) ? parsed.filter((variant) => variant && variant.id != null) : [];
  const byId = Object.fromEntries(variants.map((variant) => [String(variant.id), variant]));
  const packs = Array.from(root.querySelectorAll('input[data-vh-pack]'));
  const duo = root.querySelector('[data-vh-duo-flavors]');
  const duoOptions = Array.from(duo?.querySelectorAll('[data-vh-duo-option]') || []);
  const bar = document.querySelector('[data-vh-sticky]');
  const mainButton = root.querySelector('[data-vh-atc-main]');
  const unit = root.dataset.packUnit || '';
  const fmt = moneyFormatter(root);
  const firstServings = Number(variants[0]?.servings) || 0;
  const saveTemplate = root.dataset.saveTemplate || 'Save __P__%';

  let lastKey = '';
  let flashTimer = 0;

  const variantMap = () => {
    const el = root.querySelector('[data-stayai-variant-map]');
    return el ? parseJson(el.textContent || '{}', {}) : {};
  };

  /* A missing Duo combination is unavailable; never substitute the first variant. */
  const readState = () => {
    const variantId = String(variantInput.value);
    const variant = byId[variantId] || (duo
      ? { id: null, title: 'Unavailable', price: 0, available: false }
      : variants[0]);
    if (!variant || !Number.isFinite(Number(variant.price))) return null;
    const subInput = root.querySelector("input[name^='stayai_purchase_type'][value='subscription']");
    const subscribed = Boolean(subInput?.checked);
    const planSelect = root.querySelector('[data-stayai-plan-select]');
    const entry = variantMap()[variantId];
    let price = variant.price;
    let compareAt = variant.compare_at;
    let plan = null;
    if (subscribed && entry?.plans?.length) {
      plan = entry.plans.find((p) => String(p.id) === String(planSelect?.value)) || entry.plans[0];
      price = plan.price_cents;
      compareAt = plan.compare_at_cents > plan.price_cents ? plan.compare_at_cents : null;
    }
    const pack = variant.servings > 0 ? `${variant.servings} ${unit}` : variant.title;
    const type = subscribed && plan ? root.dataset.labelSub : root.dataset.labelOnce;
    return {
      variant,
      entry,
      subscribed: subscribed && Boolean(plan),
      detail: {
        variantId: variant.id,
        price,
        compareAtPrice: compareAt || null,
        sellingPlanId: plan ? plan.id : null,
        label: `${pack} · ${type}`,
        available: Boolean(variant.available),
      },
    };
  };

  const setText = (el, text) => {
    if (el && el.textContent !== text) el.textContent = text;
  };

  const render = () => {
    const state = readState();
    if (!state) return null;
    const { variant, detail } = state;
    const cta = root.dataset.cta || 'Add to cart';

    /* Pack cards */
    packs.forEach((pack) => {
      const checked = String(pack.value) === String(variant.id);
      pack.checked = checked;
      pack.closest('.vh-pack')?.classList.toggle('is-checked', checked);
      const badge = pack.closest('.vh-pack')?.querySelector('[data-vh-save]');
      if (badge && !pack.disabled) {
        /* The pack's own saving against buying 15-packs (computed in Liquid); never the subscription discount. */
        const percent = Number(pack.dataset.saveOnce) || 0;
        setText(badge, percent > 0 ? saveTemplate.replace('__P__', String(percent)) : '');
      }
    });
    const note = root.querySelector('[data-vh-pack-note]');
    if (note && variant.servings > 0) {
      const boxes = firstServings > 0 ? variant.servings / firstServings : 1;
      const boxText = boxes === 1 ? root.dataset.boxesOne || 'one box' : `${Number.isInteger(boxes) ? boxes : boxes.toFixed(1)} boxes`;
      setText(note, `${variant.servings} ${unit}, ${boxText}`);
    }

    /* Main add to cart */
    const label = mainButton?.querySelector('[data-vh-atc-label]');
    const sep = mainButton?.querySelector('[data-vh-atc-sep]');
    const price = mainButton?.querySelector('[data-vh-atc-price]');
    const was = mainButton?.querySelector('[data-vh-atc-was]');
    if (mainButton) {
      setText(label, variant.available ? cta : duo ? 'Unavailable' : 'Sold out');
      if (sep) sep.hidden = !variant.available;
      if (price) {
        price.hidden = !variant.available;
        setText(price, fmt(detail.price));
      }
      if (was) {
        was.hidden = !variant.available || !detail.compareAtPrice;
        setText(was, detail.compareAtPrice ? fmt(detail.compareAtPrice) : '');
      }
    }
    document.querySelectorAll('[data-vh-atc]').forEach((button) => {
      if (button.closest('form') && button.closest('form') !== form) return;
      button.disabled = !variant.available;
    });

    /* Pinned bar */
    if (bar) {
      setText(bar.querySelector('[data-vh-sticky-meta]'), detail.label);
      const stickyLabel = bar.querySelector('[data-vh-sticky-btn-label]');
      if (stickyLabel && !flashTimer) {
        const stickyCta = bar.querySelector('[data-vh-cta]')?.dataset.vhCta || 'Add';
        setText(stickyLabel, variant.available ? `${stickyCta} · ${fmt(detail.price)}` : duo ? 'Unavailable' : 'Sold out');
      }
    }

    /* Keep the address shareable, like the stock variant picker. */
    try {
      const url = new URL(window.location.href);
      if (variant.id != null && url.searchParams.get('variant') !== String(variant.id)) {
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(window.history.state, '', url);
      } else if (variant.id == null && url.searchParams.has('variant')) {
        /* A Duo pair that isn't on sale: drop the old id so a shared link can't open a different pair. */
        url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url);
      }
    } catch {
      /* ignore */
    }

    return detail;
  };

  const sync = (force) => {
    let detail = null;
    try {
      detail = render();
    } catch (error) {
      console.error('[vh-pdp]', error);
    }
    if (!detail) return;
    const key = JSON.stringify(detail);
    if (!force && key === lastKey) return;
    lastKey = key;
    window.vhPdpSelection = detail;
    document.dispatchEvent(new CustomEvent(SELECTION_EVENT, { detail }));
  };

  const schedule = () => window.setTimeout(() => sync(false), 0);

  if (duo) {
    const chooseDuo = () => {
      const values = [0, 1].map((pos) => duoOptions.find((input) => Number(input.dataset.vhDuoOption) === pos && input.checked)?.value);
      const match = variants.find((variant) => variant.options?.every((value, pos) => value === values[pos]));
      variantInput.value = match ? String(match.id) : '';
      variantInput.disabled = !match || !match.available;
      duoOptions.forEach((input) => input.closest('.vh-pack')?.classList.toggle('is-checked', input.checked));
      /* Same event as pack selection: Stay rebuilds its plan list and selling_plan for this variant. */
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      sync(false);
    };
    duoOptions.forEach((input) => input.addEventListener('change', chooseDuo));
    /* Guard before document-capture cart integrations, including direct requestSubmit(). */
    const blockUnavailable = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (event.type === 'submit' ? target !== form : target?.closest('button')?.form !== form) return;
      if (byId[String(variantInput.value)]?.available) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    window.addEventListener('click', blockUnavailable, true);
    window.addEventListener('submit', blockUnavailable, true);
  }

  packs.forEach((pack) => {
    pack.addEventListener('change', () => {
      if (!pack.checked) return;
      variantInput.value = pack.value;
      /* The Stay AI widget listens for this on the form and re-renders its prices and plans. */
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      schedule();
    });
  });

  root.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches("input[name^='stayai_purchase_type'], [data-stayai-plan-select]")) schedule();
  });

  const flashAdded = () => {
    root.querySelector('add-to-cart-component')?.animateAddToCart?.();
    const stickyLabel = bar?.querySelector('[data-vh-sticky-btn-label]');
    if (stickyLabel) {
      window.clearTimeout(flashTimer);
      stickyLabel.textContent = root.dataset.added || 'Added';
      flashTimer = window.setTimeout(() => {
        flashTimer = 0;
        try {
          render();
        } catch {
          /* the label keeps "Added"; the next selection change redraws it */
        }
      }, 1800);
    }
  };

  /*
   * One add per click. The main button is inside the form, where the Upcart app embed intercepts its click
   * and does the add (the default product page's path). The pinned bar and the science button sit outside
   * the form (form="vh-pdp-product-form"); a native submit from them would be posted by BOTH Horizon's
   * product-form (document capture) and Upcart's submit listener on the form. So their click is cancelled
   * and replayed as a click on the main button. Document capture runs before either app's listeners.
   */
  if (!document.documentElement.dataset.vhAtcProxy) {
    document.documentElement.dataset.vhAtcProxy = 'true';
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const button = target?.closest('button');
        if (!button || button.disabled) return;
        const liveForm = document.getElementById('vh-pdp-product-form');
        const main = liveForm?.querySelector('[data-vh-atc-main]');
        if (!liveForm || !main) return;
        if (button === main) {
          document.dispatchEvent(new CustomEvent('vh-pdp:atc-click'));
          return;
        }
        /* button.form resolves the form="" attribute (liveForm.id would be the <input name="id">). */
        if (button.form !== liveForm || liveForm.contains(button)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!main.disabled) main.click();
      },
      true
    );
  }
  document.addEventListener('vh-pdp:atc-click', flashAdded);

  /* Mobile pinned bar: appears once the main add-to-cart has scrolled above the viewport. */
  if (bar && mainButton && 'IntersectionObserver' in window) {
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const above = entry.boundingClientRect.top < (entry.rootBounds ? entry.rootBounds.top : 0) + 10;
          const show = !entry.isIntersecting && above;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', show ? 'false' : 'true');
          bar.inert = !show;
        });
      },
      { threshold: 0 }
    ).observe(mainButton);
  }

  /* Last: the state sync (it cannot stop the listeners above). The widget re-checks its state shortly after load. */
  sync(true);
  [400, 1000, 2000].forEach((delay) => window.setTimeout(() => sync(false), delay));
}

/* ---------- Make it a potion ---------- */

const normPlan = (name) => String(name || '').replace(/\*+\s*$/, '').trim().toLowerCase();
const lineSellingPlanId = (item) => item?.selling_plan_allocation?.selling_plan?.id ?? null;
const samePlan = (a, b) => String(a ?? '') === String(b ?? '');

function initPotion(root) {
  const box = root.querySelector('[data-vh-potion]');
  if (!box) return;
  const message = box.querySelector('[data-vh-potion-msg]');
  const say = (text) => {
    if (message) message.textContent = text || '';
  };

  const jsonFetch = async (url, body) => {
    const response = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || root.dataset.error || 'Something went wrong.');
    }
    return data;
  };

  const addItem = (item) => jsonFetch('/cart/add.js', { items: [item] });

  /* Tell the cart drawer and the cart icon; they re-render from /cart. */
  const announce = async () => {
    const cart = await jsonFetch('/cart.js');
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'vh-pdp-potion',
          data: { source: 'cart-recommended-product', itemCount: cart.item_count },
        },
      })
    );
    return cart;
  };

  /* Run one cart action. The returned text is announced in the polite live region. */
  const run = async (button, work) => {
    if (button.disabled) return;
    say('');
    const label = button.dataset.label || button.textContent;
    button.disabled = true;
    try {
      const result = await work();
      say(result);
      button.textContent = root.dataset.added || 'Added';
      button.setAttribute('aria-pressed', 'true');
      window.setTimeout(() => {
        button.textContent = label;
        button.removeAttribute('aria-pressed');
        button.disabled = false;
      }, 2200);
    } catch (error) {
      say(error?.message || root.dataset.error || 'Something went wrong.');
      button.disabled = false;
    }
  };

  box.querySelectorAll('[data-vh-addon]').forEach((button) => {
    button.addEventListener('click', () =>
      run(button, async () => {
        await addItem({ id: Number(button.dataset.variantId), quantity: 1 });
        await announce();
        return `Added ${button.dataset.title || ''}`.trim();
      })
    );
  });

  /* The page's current plan, by name, from the Stay AI widget's own variant map. */
  const currentPlanName = (variantId, planId) => {
    const el = root.querySelector('[data-stayai-variant-map]');
    const map = el ? parseJson(el.textContent || '{}', {}) : {};
    return map[String(variantId)]?.plans?.find((p) => String(p.id) === String(planId))?.name || '';
  };

  const duo = box.querySelector('.vh-duo');
  const duoButton = duo?.querySelector('[data-vh-duo]');
  if (!duo || !duoButton) return;

  const fmt = moneyFormatter(root);
  const duoPlans = parseJson(duo.querySelector('[data-vh-duo-plans]')?.textContent || '[]', []);
  const duoVariantId = Number(duoButton.dataset.variantId);
  const duoAvailable = duoButton.dataset.available === 'true';
  const duoTitle = duoButton.dataset.title || '';
  const labels = {
    add: duoButton.dataset.addLabel || 'Add the duo',
    swap: duoButton.dataset.swapLabel || 'Swap for the duo',
    swapped: duoButton.dataset.swappedLabel || 'Swapped',
    added: root.dataset.added || 'Added',
  };
  const nowEl = duo.querySelector('[data-vh-duo-now]');
  const wasEl = duo.querySelector('[data-vh-duo-was]');
  const termsEl = duo.querySelector('[data-vh-duo-terms]');
  const setText = (el, text) => {
    if (el && el.textContent !== text) el.textContent = text;
  };

  /* "Delivered every 30 days*" -> "every 30 days". */
  const intervalText = (name) => {
    const clean = String(name || '').replace(/\*+\s*$/, '').trim();
    const every = clean.match(/every\s.+$/i);
    return every ? every[0].toLowerCase() : `${clean.charAt(0).toLowerCase()}${clean.slice(1)}`;
  };

  /*
   * Exactly what a tap will add, for a selection and the cart's lines: the duo on the selected purchase type
   * (a subscription uses the duo's plan with the same delivery interval), at the quantity of the ONE matching
   * cart line it replaces, or 1 when there is none. Null when the duo has no plan on the selected interval.
   */
  const offerFor = (selection, items) => {
    const planId = selection.sellingPlanId ?? null;
    let duoPlan = null;
    if (planId) {
      const name = normPlan(currentPlanName(selection.variantId, planId));
      duoPlan = duoPlans.find((plan) => normPlan(plan.name) === name) || null;
      if (!duoPlan) return null;
    }
    const match =
      items.find(
        (item) => Number(item.variant_id) === Number(selection.variantId) && samePlan(lineSellingPlanId(item), planId)
      ) || null;
    const quantity = match ? match.quantity : 1;
    const reference = Number(duo.dataset.refPrice) || 0;
    const price = duoPlan ? Number(duoPlan.price) : Number(duo.dataset.oncePrice) || 0;
    const was = duoPlan ? (reference > price ? reference : 0) : Number(duo.dataset.onceWas) || 0;
    const type = duoPlan
      ? `${root.dataset.labelSub || 'Subscribe & Save'}, ${intervalText(duoPlan.name)}`
      : root.dataset.labelOnce || 'One-time';
    const terms = quantity > 1 ? `${type}, quantity ${quantity}` : type;
    return {
      planId,
      duoPlan,
      match,
      quantity,
      price,
      was,
      terms,
      key: [duoPlan ? duoPlan.id : 'once', match ? match.key : 'none', quantity].join('|'),
    };
  };

  let cartItems = null; /* null until /cart.js has answered; the button stays disabled until then */
  let shownKey = null;
  let busy = false;
  let done = null; /* { selectionKey, label } after a successful swap or add, until the selection changes */
  let cartRequest = 0;

  /* Rewrite the row (price, purchase type, button) for the current selection and cart. */
  const renderDuo = () => {
    const selection = window.vhPdpSelection;
    const offer = selection ? offerFor(selection, cartItems || []) : null;
    duo.hidden = Boolean(selection) && !offer;
    if (!offer) {
      shownKey = null;
      duoButton.disabled = true;
      return;
    }
    setText(nowEl, fmt(offer.price));
    if (wasEl) {
      wasEl.hidden = !offer.was;
      setText(wasEl, offer.was ? fmt(offer.was) : '');
    }
    setText(termsEl, offer.terms);
    if (done && done.selectionKey !== JSON.stringify(window.vhPdpSelection)) done = null;
    if (done) {
      setText(duoButton, done.label);
      duoButton.setAttribute('aria-pressed', 'true');
      duoButton.disabled = true;
      return;
    }
    duoButton.removeAttribute('aria-pressed');
    setText(duoButton, offer.match ? labels.swap : labels.add);
    shownKey = cartItems === null ? null : offer.key;
    duoButton.disabled = busy || !duoAvailable || shownKey === null;
  };

  const refreshCart = async () => {
    const request = ++cartRequest;
    let items = null;
    try {
      items = (await jsonFetch('/cart.js')).items || [];
    } catch {
      items = null;
    }
    if (request !== cartRequest) return;
    cartItems = items;
    renderDuo();
  };

  document.addEventListener(SELECTION_EVENT, (event) => {
    if (done && done.selectionKey !== JSON.stringify(event.detail || {})) done = null;
    renderDuo();
    refreshCart();
  });
  /* A line added elsewhere on the page (main button, pinned bar, cart drawer) can turn "add" into "swap". */
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.sourceId === 'vh-pdp-potion') return;
    refreshCart();
  });
  /*
   * Upcart does the main add itself and fires no cart:update, so also follow every finished cart write on the
   * page (any script, any app) by its network entry. Skipped while this row is mid-swap; it re-reads after.
   */
  if ('PerformanceObserver' in window) {
    let pending = 0;
    try {
      new PerformanceObserver((list) => {
        const wrote = list
          .getEntries()
          .some((entry) => /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(new URL(entry.name, location.href).pathname + '?'));
        if (!wrote || busy) return;
        window.clearTimeout(pending);
        pending = window.setTimeout(refreshCart, 150);
      }).observe({ type: 'resource', buffered: false });
    } catch {
      /* no resource timing: cart:update above still covers Horizon's own adds */
    }
  }
  renderDuo();
  refreshCart();

  /*
   * Swap the ONE cart line that matches the current selection (variant + selling plan) for the duo, or add
   * the duo when there is none. The cart is re-read first; if what the tap would do differs from what the row
   * shows, nothing is changed and the row is redrawn. The duo goes in first; if removing the old line then
   * fails, the duo is taken back out, so a failure leaves the cart as it was.
   */
  duoButton.addEventListener('click', async () => {
    if (duoButton.disabled || busy || done) return;
    const selection = window.vhPdpSelection || {};
    const expected = shownKey;
    busy = true;
    duoButton.disabled = true;
    say('');
    try {
      const before = await jsonFetch('/cart.js');
      const items = before.items || [];
      cartRequest += 1;
      cartItems = items;
      const offer = offerFor(selection, items);
      if (!offer) throw new Error(root.dataset.duoNoPlan || 'The duo is not offered on that delivery schedule.');
      if (offer.key !== expected) {
        say(root.dataset.duoChanged || 'Your cart changed. Check the duo price and tap again.');
        return;
      }
      const { match, quantity, duoPlan } = offer;

      const existing = items.find(
        (item) => Number(item.variant_id) === duoVariantId && samePlan(lineSellingPlanId(item), duoPlan ? duoPlan.id : null)
      );
      const request = { id: duoVariantId, quantity };
      if (duoPlan) request.selling_plan = duoPlan.id;
      const added = await addItem(request);
      const addedKey = added.items?.[0]?.key || existing?.key;

      if (match) {
        try {
          await jsonFetch('/cart/change.js', { id: match.key, quantity: 0 });
        } catch (error) {
          try {
            await jsonFetch('/cart/change.js', { id: addedKey, quantity: existing ? existing.quantity : 0 });
          } catch {
            /* nothing more can be done; the message below still tells the shopper */
          }
          await announce().catch(() => {});
          throw new Error(root.dataset.error || error.message);
        }
      }
      done = { selectionKey: JSON.stringify(selection), label: match ? labels.swapped : labels.added };
      const after = await announce().catch(() => null);
      if (after) cartItems = after.items || [];
      say(`${match ? 'Swapped for' : 'Added'} ${duoTitle}: ${offer.terms}, ${fmt(offer.price)}`);
    } catch (error) {
      say(error?.message || root.dataset.error || 'Something went wrong.');
      refreshCart();
    } finally {
      busy = false;
      renderDuo();
    }
  });
}

/* ---------- Links to sibling sections ---------- */

function initAnchors(root) {
  root.querySelectorAll('[data-vh-anchor]').forEach((link) => {
    const href = link.getAttribute('href') || '';
    const id = href.startsWith('#') ? href.slice(1) : '';
    if (id && document.getElementById(id)) return;
    /* No target on this page: never leave a dead link. */
    if (link.classList.contains('vh-rating')) link.removeAttribute('href');
    else link.hidden = true;
  });
}

function initAll() {
  document.querySelectorAll('[data-vh-pdp]').forEach((root) => {
    if (root.dataset.vhInit === 'true') return;
    root.dataset.vhInit = 'true';
    /* Each part stands alone: a failure in one never leaves the others uninitialised. */
    [initGallery, initPurchase, initPotion, initAnchors].forEach((init) => {
      try {
        init(root);
      } catch (error) {
        console.error('[vh-pdp]', init.name, error);
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
document.addEventListener('shopify:section:load', initAll);
